import { z } from 'zod';
/**
 * The LTO archive (Αρχείο Video as a tape MAM). Every archived item is a row of the video archive on a tape; a tape
 * is an LTO cartridge known by its barcode (the code the archive has always written, e.g. MS0050). When a file is
 * archived, the archive agent at the station (next to the tape drive) writes it to the tape (LTFS), keeps its size and
 * checksum, and sends AirPost a small preview copy (proxy) and a picture, so the newsroom can watch an item before
 * anyone loads the tape. A restore asks the agent to bring the file (or a part of it, IN–OUT on the preview) back
 * from tape to one of the places the agent delivers to (the playout's watch folder, the edit storage).
 */
/** LTO generations and their native (uncompressed) capacity and speed, as the LTO Program publishes them. */
export const LTO_GENERATIONS = ['LTO-5', 'LTO-6', 'LTO-7', 'LTO-8', 'LTO-9'];
export const LTO_NATIVE = {
    'LTO-5': { capacityBytes: 1.5e12, mbPerS: 140 },
    'LTO-6': { capacityBytes: 2.5e12, mbPerS: 160 },
    'LTO-7': { capacityBytes: 6e12, mbPerS: 300 },
    'LTO-8': { capacityBytes: 12e12, mbPerS: 360 },
    'LTO-9': { capacityBytes: 18e12, mbPerS: 400 },
};
/** Where a cartridge is: in a slot of the tape library, in the drive, on a shelf of the archive room, off site (the vault copy). */
export const TAPE_LOCATIONS = ['library', 'drive', 'shelf', 'offsite', 'unknown'];
/** In use (more can be written), full, or out of service. */
export const TAPE_STATES = ['active', 'full', 'retired'];
/** A restore: asked, the tape being fetched and loaded, read from tape, delivered; or failed / cancelled. */
export const RESTORE_STATUSES = ['requested', 'loading', 'reading', 'done', 'failed', 'cancelled'];
export const RESTORE_OPEN = ['requested', 'loading', 'reading'];
export const isRestoreOpen = (s) => RESTORE_OPEN.includes(s);
/** A tape barcode: letters and digits as on the label (LTO labels add the media code, e.g. MS0050L8; the archive writes MS0050). */
export const BARCODE = /^[A-Z0-9][A-Z0-9-]{1,15}$/;
export const normaliseBarcode = (s) => s.trim().toUpperCase();
/** What the agent learned about a file (ffprobe). */
export const archiveMediaInfoSchema = z.object({
    container: z.string().max(40).nullable(),
    videoCodec: z.string().max(40).nullable(),
    width: z.number().int().min(0).max(16384).nullable(),
    height: z.number().int().min(0).max(16384).nullable(),
    fps: z.number().min(0).max(240).nullable(),
    scan: z.enum(['progressive', 'interlaced']).nullable(),
    audioCodec: z.string().max(40).nullable(),
    audioChannels: z.number().int().min(0).max(64).nullable(),
    durationMs: z.number().int().min(0),
    bitrate: z.number().int().min(0).nullable(),
});
/** The agent tells AirPost a file is on a tape: the tape, the file, and the catalogue fields (a new item, or an existing one by id). */
export const agentItemSchema = z.object({
    tape: z.object({
        barcode: z.string().transform(normaliseBarcode).pipe(z.string().regex(BARCODE)),
        generation: z.enum(LTO_GENERATIONS).nullable().optional(),
        usedBytes: z.number().int().min(0).optional(),
        capacityBytes: z.number().int().min(0).optional(),
    }),
    /** An item already in the catalogue (the archivist described it first); otherwise a new one is made. */
    videoId: z.uuid().optional(),
    file: z.object({
        name: z.string().min(1).max(255),
        size: z.number().int().min(0),
        /** `sha256:<hex>`. */
        checksum: z.string().regex(/^sha256:[0-9a-f]{64}$/),
        /** The path inside the tape's file system. */
        tapePath: z.string().min(1).max(1000),
        media: archiveMediaInfoSchema,
    }),
    item: z
        .object({
        title: z.string().trim().min(1).max(250),
        eventDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        videoType: z.enum(['edited', 'raw']).nullable().optional(),
        itemNo: z.number().int().min(0).max(999_999).nullable().optional(),
        summary: z.string().max(100_000).nullable().optional(),
    })
        .optional(),
    /** Seed rows of the demo only. */
    isDemo: z.boolean().optional(),
});
/** Where the agent can deliver a restored file (it announces them; the newsroom picks one). */
export const agentTargetSchema = z.object({ key: z.string().regex(/^[a-z0-9-]{1,30}$/), label: z.string().min(1).max(80) });
export const agentHelloSchema = z.object({
    version: z.string().max(40),
    /** `ltfs` (a mounted LTFS tape) or `simulator` (a folder playing the tape, for tests and the demo). */
    backend: z.enum(['ltfs', 'simulator']),
    targets: z.array(agentTargetSchema).max(10),
    /** The tape in the drive now, when the agent can read its label. */
    loaded: z.string().max(20).nullable(),
});
/** The agent reports how a restore goes. */
export const agentRestoreUpdateSchema = z.object({
    status: z.enum(['loading', 'reading', 'done', 'failed']),
    progress: z.number().min(0).max(1).optional(),
    message: z.string().max(500).nullable().optional(),
    outputName: z.string().max(255).nullable().optional(),
    bytes: z.number().int().min(0).nullable().optional(),
});
/** A restore moves only forward (requested → loading → reading → done), or ends failed / cancelled while it is open. */
export function restoreStepAllowed(from, to) {
    if (!isRestoreOpen(from))
        return false;
    if (to === 'failed' || to === 'cancelled')
        return true;
    const order = ['requested', 'loading', 'reading', 'done'];
    return order.indexOf(to) >= order.indexOf(from);
}
/** The name a restored part gets: the file's name with its range (a whole file keeps its name). */
export function restoreOutputName(fileName, inMs, outMs) {
    if (inMs === null || outMs === null)
        return fileName;
    const dot = fileName.lastIndexOf('.');
    const base = dot > 0 ? fileName.slice(0, dot) : fileName;
    const ext = dot > 0 ? fileName.slice(dot) : '';
    const tc = (ms) => {
        const s = Math.floor(ms / 1000);
        return `${String(Math.floor(s / 3600)).padStart(2, '0')}${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}${String(s % 60).padStart(2, '0')}`;
    };
    return `${base}_${tc(inMs)}-${tc(outMs)}${ext}`;
}
/** Bytes as the archive speaks of them (decimal, like tape capacities). */
export function formatBytes(n) {
    if (n === null || n === undefined)
        return '—';
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    let v = n;
    let u = 0;
    while (v >= 1000 && u < units.length - 1) {
        v /= 1000;
        u++;
    }
    return `${v >= 100 || u === 0 ? Math.round(v) : v.toFixed(1)} ${units[u]}`;
}
/**
 * Who made an item's preview copy: the agent (from the file it wrote to tape), the browser (from a file it could read,
 * picked in the item's card: only the preview was sent), or the server (from a file sent to it for that, then deleted).
 */
export const PREVIEW_SOURCES = ['agent', 'browser', 'server'];
/** A file sent to the server for its preview goes in pieces of this size, in order. */
export const INGEST_CHUNK_BYTES = 8 * 1024 * 1024;
export const ingestStartSchema = z.object({ name: z.string().trim().min(1).max(255), size: z.number().int().min(1) }).strict();
/** A file sent to the server for its preview: arriving, waiting for its turn, read, made into the preview, kept; or failed. */
export const INGEST_STAGES = ['receiving', 'queued', 'probing', 'encoding', 'storing', 'done', 'failed'];
//# sourceMappingURL=archive.js.map